import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { SLUG_PATTERN } from "../../../affiliate/affiliateService";
import { AffiliateSummary, affiliateAdminApi, apiErrorMessage } from "./affiliateAdminApi";

export const affiliateQueryKeys = {
  list: ["admin", "affiliates"] as const,
  detail: (affiliateId: number) => ["admin", "affiliates", affiliateId] as const,
};

export function StatusChip({ status }: { status: AffiliateSummary["status"] }) {
  return (
    <Chip
      size="small"
      label={status === "active" ? "Active" : "Suspended"}
      color={status === "active" ? "success" : "default"}
      variant={status === "active" ? "filled" : "outlined"}
    />
  );
}

const columns: GridColDef<AffiliateSummary>[] = [
  { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
  { field: "slug", headerName: "Slug", flex: 1, minWidth: 140 },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    renderCell: (params) => <StatusChip status={params.row.status} />,
  },
  {
    field: "hostname",
    headerName: "Hostname",
    flex: 1,
    minWidth: 180,
    valueGetter: (_value, row) => row.hostname ?? "—",
  },
  {
    field: "activeBrandingVersion",
    headerName: "Branding",
    width: 130,
    valueGetter: (_value, row) => (row.activeBrandingVersion ? `v${row.activeBrandingVersion}` : "Not published"),
  },
];

function CreateAffiliateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<{ name: string; slug: string }>({
    defaultValues: { name: "", slug: "" },
  });

  const create = useMutation({
    mutationFn: (values: { name: string; slug: string }) => affiliateAdminApi.create(values.slug, values.name),
    onSuccess: (detail) => {
      reset();
      onClose();
      navigate(`/admin/affiliates/${detail.affiliateId}`);
    },
  });

  const close = () => {
    reset();
    create.reset();
    onClose();
  };

  const { ref: nameRef, ...nameField } = register("name", {
    required: "Enter the affiliate's name.",
    maxLength: { value: 200, message: "Use 200 characters or fewer." },
  });
  const { ref: slugRef, ...slugField } = register("slug", {
    required: "Enter a slug.",
    setValueAs: (value: string) => value.trim().toLowerCase(),
    pattern: { value: SLUG_PATTERN, message: "Use 2-63 lowercase letters, numbers or hyphens." },
  });

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <Box component="form" noValidate onSubmit={handleSubmit((values) => create.mutate(values))}>
        <DialogTitle>New affiliate</DialogTitle>
        <DialogContent>
          {create.isError && (
            <Alert severity="error" role="alert" sx={{ mb: 2 }}>
              {apiErrorMessage(create.error)}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              {...nameField}
              inputRef={nameRef}
              label="Name"
              required
              autoFocus
              error={!!errors.name}
              helperText={errors.name?.message}
            />
            <TextField
              {...slugField}
              inputRef={slugRef}
              label="Slug"
              required
              error={!!errors.slug}
              helperText={errors.slug?.message ?? "Used in ?affiliate= links and cannot be changed later."}
              slotProps={{ htmlInput: { autoCapitalize: "none", spellCheck: false } }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            New affiliates are active, and go live once branding is published.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={create.isPending}>
            Create
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default function AffiliatesAdminPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const affiliates = useQuery({ queryKey: affiliateQueryKeys.list, queryFn: affiliateAdminApi.list });

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h6" component="h2">
            Affiliates
          </Typography>
          <Typography color="text.secondary">
            Branded sign-in pages for partner domains.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New affiliate
        </Button>
      </Box>

      {affiliates.isError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {apiErrorMessage(affiliates.error, "Affiliates could not be loaded.")}
        </Alert>
      )}

      <DataGrid
        rows={affiliates.data ?? []}
        columns={columns}
        getRowId={(row) => row.affiliateId}
        loading={affiliates.isLoading}
        autoHeight
        disableRowSelectionOnClick
        onRowClick={(params) => navigate(`/admin/affiliates/${params.row.affiliateId}`)}
        sx={{ bgcolor: "background.paper", "& .MuiDataGrid-row": { cursor: "pointer" } }}
        localeText={{ noRowsLabel: "No affiliates yet" }}
      />

      <CreateAffiliateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}
